<template>
  <main class="bb-home">

    <section id="top" class="bb-hero">
      <div class="bb-hero-dots"></div>
      <div class="bb-hero-glow"></div>
      <div class="bb-container bb-hero-inner">
        <div class="bb-hero-badge">
          <span class="bb-hero-badge-tag">NEW</span>
          <span class="bb-hero-badge-txt">Construction technology, end-to-end</span>
        </div>

        <h1 class="bb-hero-headline">
          Solving the toughest problems in construction <span class="bb-accent">using technology</span>
        </h1>

        <p class="bb-hero-sub">
          One platform trusted by India's largest developers and PMCs — for AI project monitoring,
          lifecycle cost management, and manpower mobilization.
        </p>

        <div class="bb-hero-actions">
          <a
            :href="requestDemo.url"
            target="_blank"
            rel="noopener noreferrer"
            class="bb-cta-btn"
            @click="sendInformationToGoogleAnalitics('request_demo')">
            Request Demo
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 9h12M11 5l4 4-4 4" stroke="#003366" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
          <a href="#solutions" class="bb-ghost-btn">Explore Solutions</a>
        </div>

        <div class="bb-hero-stats">
          <div v-for="stat in heroStats" :key="stat.txt" class="bb-hero-stat">
            <div class="bb-hero-stat-val">{{ stat.val }}</div>
            <div class="bb-hero-stat-txt">{{ stat.txt }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="bb-marquee">
      <p class="bb-marquee-title">Trusted by India's leading developers &amp; PMCs</p>
      <InfiniteScroll :clients="homeClients" />
    </section>

    <section id="solutions" class="bb-section bb-bg-alt bb-overview">
      <div class="bb-container">
        <div class="bb-section-header">
          <div class="bb-section-pill">What Bandhoo Does</div>
          <h2 class="bb-section-title">Four solutions for the entire project lifecycle</h2>
          <p class="bb-section-sub">
            From the first survey to final handover and payment — Bandhoo's technology spans every
            stage of construction.
          </p>
          <div class="bb-section-rule"></div>
        </div>

        <div class="bb-pillars-grid">
          <component
            :is="pillar.external ? 'a' : 'RouterLink'"
            v-for="pillar in pillars"
            :key="pillar.name"
            v-bind="pillar.external ? { href: pillar.url, target: '_blank', rel: 'noopener noreferrer' } : { to: pillar.url }"
            class="bb-pillar-card"
            @click="sendInformationToGoogleAnalitics(pillar.gtag)">
            <div class="bb-pillar-card-top">
              <div class="bb-pillar-icon">
                <svg width="30" height="30" viewBox="-20 -20 40 40" v-html="pillar.icon"></svg>
              </div>
              <span class="bb-pillar-tag">{{ pillar.tag }}</span>
            </div>
            <h3 class="bb-pillar-name">{{ pillar.name }}</h3>
            <p class="bb-pillar-desc">{{ pillar.desc }}</p>
            <span class="bb-learn-more">Learn more <span class="bb-arrow">→</span></span>
          </component>
        </div>
      </div>
    </section>

    <section id="constra" class="bb-section bb-bg-white">
      <div class="bb-container">
        <div class="bb-pillar">
          <div>
            <div class="bb-pillar-eyebrow">
              <span class="bb-pillar-num">01</span>
              <span class="bb-pillar-tagline">Build &amp; Survey</span>
            </div>
            <h3 class="bb-pillar-heading">CONSTRA</h3>
            <p class="bb-pillar-lede">AI-based construction management, powered by drones &amp; 360° cameras.</p>
            <p class="bb-pillar-body">
              A 3-in-1 solution for construction project management that integrates with
              <strong>Autodesk Construction Cloud</strong> and <strong>Microsoft Project</strong> —
              with zero manual data entry.
            </p>
            <div class="bb-checklist">
              <div v-for="item in constraPoints" :key="item" class="bb-check-row">
                <span class="bb-check">✓</span>
                <span class="bb-check-txt">{{ item }}</span>
              </div>
            </div>
            <RouterLink
              to="/solutions/ai_project_monitoring"
              class="bb-pillar-cta"
              @click="sendInformationToGoogleAnalitics('ai_project_monitoring')">
              Explore CONSTRA <span class="bb-arrow">→</span>
            </RouterLink>
          </div>

          <div class="bb-pillar-visual bb-constra-visual">
            <div class="bb-constra-frame">
              <img src="@/assets/img/drone_construction.jpg" alt="Drone monitoring a construction site" class="bb-constra-img" />
              <div class="bb-constra-scrim"></div>
            </div>
            <div class="bb-chip bb-chip-tl">
              <div class="bb-chip-title">AI Progress</div>
              <div class="bb-chip-sub">against WBS</div>
            </div>
            <div class="bb-chip bb-chip-br">
              <div class="bb-chip-title">360° + Drone</div>
              <div class="bb-chip-sub">every stage</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="plm" class="bb-section bb-bg-alt">
      <div class="bb-container">
        <div class="bb-pillar">
          <div>
            <div class="bb-pillar-eyebrow">
              <span class="bb-pillar-num">02</span>
              <span class="bb-pillar-tagline">Cost Manager</span>
            </div>
            <h3 class="bb-pillar-heading">PLM — Project Lifecycle Manager</h3>
            <p class="bb-pillar-lede">The only cloud SaaS that manages costs end-to-end, across every project stage.</p>
            <p class="bb-pillar-body">
              PLM digitizes every stage of cost management. Three modules — linkable to your
              <strong>ERP / SAP</strong> — deliver a complete digital transformation.
            </p>
            <div class="bb-mods">
              <div v-for="mod in plmModules" :key="mod.code" class="bb-mod">
                <div class="bb-mod-code">{{ mod.code }}</div>
                <div class="bb-mod-txt">{{ mod.name }}</div>
              </div>
            </div>
            <RouterLink to="/solutions/developer" class="bb-pillar-cta" @click="sendInformationToGoogleAnalitics('website_developer')">
              See PLM for Developers <span class="bb-arrow">→</span>
            </RouterLink>
          </div>

          <div class="bb-pillar-visual">
            <div class="bb-cmp">
              <div class="bb-cmp-row bb-cmp-head">
                <div class="bb-cmp-activity">Activity</div>
                <div class="bb-cmp-without">Without Bandhoo</div>
                <div class="bb-cmp-with">With Bandhoo</div>
              </div>
              <div v-for="(row, index) in plmComparison" :key="row.task" class="bb-cmp-row bb-cmp-body" :class="{ odd: index % 2 }">
                <div class="bb-cmp-task">{{ row.task }}</div>
                <div class="bb-cmp-bad">{{ row.without }}</div>
                <div class="bb-cmp-good">{{ row.with }}</div>
              </div>
              <div class="bb-cmp-foot">A complete digital transformation</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="manpower" class="bb-section bb-bg-white">
      <div class="bb-container">
        <div class="bb-section-header">
          <div class="bb-section-pill">03 · Manpower Staffing (NMR) &amp; Subcontractor Hiring</div>
          <h2 class="bb-section-title">Mobilize the right workforce — two ways</h2>
          <p class="bb-section-sub">
            Let Bandhoo staff and run your workforce end-to-end, or tap our network directly to find
            a labour contractor yourself.
          </p>
          <div class="bb-section-rule"></div>
        </div>

        <div class="bb-pillar">
          <div>
            <h3 class="bb-sub-heading">One network, every tier of the workforce</h3>
            <p class="bb-pillar-body">
              Whatever the scale, we reach the right people — then take care of compliance,
              deployment and payroll so your site keeps moving.
            </p>
            <div class="bb-checklist">
              <div v-for="item in manpowerPoints" :key="item" class="bb-check-row">
                <span class="bb-check">✓</span>
                <span class="bb-check-txt">{{ item }}</span>
              </div>
            </div>
            <div class="bb-edge">
              <span class="bb-edge-tag">★ Edge</span>
              <div>
                <div class="bb-edge-title">Proprietary outreach technology</div>
                <div class="bb-edge-txt">
                  In-house <strong>IVR, SMS &amp; WhatsApp</strong> reaches thousands of workers a
                  day — unmatched sourcing velocity.
                </div>
              </div>
            </div>
          </div>

          <div class="bb-pillar-visual">
            <div class="bb-pyramid">
              <div class="bb-pyramid-title">The construction workforce pyramid</div>
              <div class="bb-pyramid-sub">We reach every level — from large contractors to individual workers</div>
              <div class="bb-pyramid-tiers">
                <div
                  v-for="tier in workforceTiers"
                  :key="tier.label"
                  class="bb-tier"
                  :style="{ width: tier.width + '%' }">
                  <div class="bb-tier-val">{{ tier.val }}</div>
                  <div class="bb-tier-label">{{ tier.label }}</div>
                  <div class="bb-tier-sub">{{ tier.sub }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bb-trades">
          <p class="bb-trades-title">Trades we staff</p>
          <div class="bb-trades-chips">
            <span v-for="trade in trades" :key="trade" class="bb-trade-chip">{{ trade }}</span>
          </div>
        </div>

        <div class="bb-offers">
          <div class="bb-offer bb-offer-dark">
            <h3 class="bb-offer-title bb-offer-title-light">Manpower Staffing</h3>
            <p class="bb-offer-txt bb-offer-txt-light">
              We source, deploy and manage skilled &amp; unskilled workers on your site — with full
              compliance, payroll and batch mobilization handled by Bandhoo.
            </p>
            <RouterLink to="/solutions/contractor" class="bb-offer-cta-lime" @click="sendInformationToGoogleAnalitics('website_contractor')">
              Explore Staffing →
            </RouterLink>
          </div>
          <div class="bb-offer bb-offer-light">
            <h3 class="bb-offer-title">Find a Labour Contractor</h3>
            <p class="bb-offer-txt">
              Prefer to engage a contractor directly? Search our network of 1.5 lakh+ labour
              contractors across every trade and major city, and connect in minutes.
            </p>
            <RouterLink to="/findcontractors" class="bb-offer-cta-navy">
              Find Contractors →
            </RouterLink>
          </div>
        </div>
      </div>
    </section>

    <section id="pmc" class="bb-section bb-bg-alt">
      <div class="bb-container">
        <div class="bb-pillar">
          <div>
            <div class="bb-pillar-eyebrow">
              <span class="bb-pillar-num">04</span>
              <span class="bb-pillar-tagline">Managed Service</span>
            </div>
            <h3 class="bb-pillar-heading">Digital PMC</h3>
            <p class="bb-pillar-lede">When you need more than software, Bandhoo runs your project as a tech-enabled PMC.</p>
            <p class="bb-pillar-body">
              Our on-ground project managers execute day-to-day work on Bandhoo's own digital stack —
              giving you a single accountable partner with complete, real-time visibility into
              <strong>progress, cost and quality</strong>.
            </p>
            <div class="bb-checklist">
              <div v-for="item in pmcPoints" :key="item" class="bb-check-row">
                <span class="bb-check">✓</span>
                <span class="bb-check-txt">{{ item }}</span>
              </div>
            </div>
            <a
              :href="requestDemo.url"
              target="_blank"
              rel="noopener noreferrer"
              class="bb-pillar-cta"
              @click="sendInformationToGoogleAnalitics('request_demo')">
              Talk to our PMC team <span class="bb-arrow">→</span>
            </a>
          </div>

          <div class="bb-pillar-visual">
            <div class="bb-stack">
              <div class="bb-stack-title">One managed service, three engines</div>
              <div class="bb-stack-list">
                <div v-for="engine in pmcStack" :key="engine.name" class="bb-stack-row">
                  <span class="bb-stack-num">{{ engine.code }}</span>
                  <div>
                    <div class="bb-stack-name">{{ engine.name }}</div>
                    <div class="bb-stack-desc">{{ engine.desc }}</div>
                  </div>
                </div>
              </div>
              <div class="bb-stack-note">
                <span class="bb-stack-note-arrow">→</span>
                <div>Delivered by your dedicated Bandhoo PMC team</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="team" class="bb-section bb-bg-white">
      <div class="bb-container bb-container-narrow">
        <div class="bb-section-header">
          <div class="bb-section-pill">Team</div>
          <h2 class="bb-section-title">Built by operators who know construction &amp; technology</h2>
          <p class="bb-section-sub">
            Bandhoo is led by a team with deep experience across global consulting, real estate, and
            world-class engineering.
          </p>
          <div class="bb-section-rule"></div>
        </div>

        <div class="bb-founders-grid">
          <div v-for="founder in founders" :key="founder.name" class="bb-founder">
            <div class="bb-founder-head">
              <img :src="founder.img" :alt="founder.name" class="bb-founder-img" :class="founder.imgClass" />
              <div class="bb-founder-meta">
                <div class="bb-founder-name-row">
                  <span class="bb-founder-name">{{ founder.name }}</span>
                  <a :href="founder.li" target="_blank" rel="noopener noreferrer" class="bb-li" :title="`${founder.name} on LinkedIn`">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
                    </svg>
                  </a>
                </div>
                <div class="bb-founder-role">{{ founder.role }}</div>
                <div class="bb-founder-bio">{{ founder.bio }}</div>
              </div>
            </div>
            <div class="bb-founder-points">
              <div v-for="point in founder.points" :key="point" class="bb-founder-point">
                <span class="bb-founder-tick">✓</span>
                <span>{{ point }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="bb-team">
          <p class="bb-team-title">Leadership Team</p>
          <div class="bb-team-grid">
            <div v-for="member in leadership" :key="member.name" class="bb-team-card">
              <img :src="`/img/team/${member.image}`" :alt="member.name" class="bb-team-img" />
              <div class="bb-team-meta">
                <div class="bb-team-name">{{ member.name }}</div>
                <div class="bb-team-role">{{ member.role }}</div>
              </div>
              <a :href="member.li" target="_blank" rel="noopener noreferrer" class="bb-li bb-li-lg" :title="`${member.name} on LinkedIn`">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="blog" class="bb-section bb-bg-alt">
      <div class="bb-container">
        <div class="bb-section-header">
          <div class="bb-section-pill">Blog</div>
          <h2 class="bb-section-title">The Bandhoo Blog</h2>
          <p class="bb-section-sub">
            Posts and long-form articles from Prashant Gupta on construction technology, manpower,
            and building Bandhoo — latest first.
          </p>
          <div class="bb-section-rule"></div>
        </div>

        <div class="bb-blog-grid">
          <RouterLink
            v-for="post in latestPosts"
            :key="post.num"
            to="/blog#posts"
            class="bb-blog-card">
            <div class="bb-blog-stripe" :style="{ background: `linear-gradient(90deg, ${topicColor(post.topic)}, var(--bb-navy-light))` }"></div>
            <div class="bb-blog-body">
              <div class="bb-blog-meta">
                <span class="bb-blog-topic" :style="{ color: topicColor(post.topic), background: topicColor(post.topic) + '16' }">{{ post.topic }}</span>
                <span class="bb-blog-date">{{ post.date }}</span>
              </div>
              <p class="bb-blog-lead">{{ lead(post.paras[0]) }}</p>
              <p class="bb-blog-excerpt">{{ post.paras[1] || post.paras[0] }}</p>
              <div class="bb-blog-read">Read post →</div>
            </div>
          </RouterLink>
        </div>
        <p class="bb-blog-more">
          <RouterLink to="/blog" class="bb-blog-more-btn">
            Read the blog&nbsp;→
          </RouterLink>
        </p>
      </div>
    </section>

    <section id="trust" class="bb-trust">
      <div class="bb-trust-inner">
        <div class="bb-quote">
          <div class="bb-quote-bar"></div>
          <p class="bb-quote-txt">
            Bandhoo makes life simpler for <strong>Builders, Contractors, Material Suppliers,
            Architects, Foremen, and Workers</strong>
          </p>
        </div>

        <div class="bb-stamps">
          <div class="bb-stamp"><img src="@/assets/img/soc.png" alt="AICPA SOC 2 Certified" /></div>
          <div class="bb-stamp"><img src="@/assets/img/iso.png" alt="ISO 27001 Certified" /></div>
        </div>

        <div class="bb-download">
          <div class="bb-quote">
            <div class="bb-quote-bar"></div>
            <p class="bb-quote-txt bb-quote-txt-sm">
              Download Bandhoo app for Android or iOS or Web from the appropriate link below.
            </p>
          </div>
          <div class="bb-store-badges">
            <a
              v-for="app in appUtils"
              :key="app.gtag"
              :href="app.downloadLink"
              target="_blank"
              rel="noopener noreferrer"
              class="bb-store-badge"
              @click="sendInformationToGoogleAnalitics(app.gtag)">
              <img :src="require(`../assets/img/${app.image}`)" alt="Download the Bandhoo app" />
            </a>
          </div>
        </div>
      </div>
    </section>

    <section id="demo" class="bb-footer-cta">
      <div class="bb-hero-dots"></div>
      <div class="bb-footer-cta-glow"></div>
      <div class="bb-footer-cta-inner">
        <div class="bb-footer-cta-eyebrow">Get Started</div>
        <h2 class="bb-footer-cta-title">Let's solve your toughest construction problems</h2>
        <p class="bb-footer-cta-sub">
          Talk to our team about project monitoring, lifecycle cost management, or mobilizing
          manpower for your sites.
        </p>
        <div class="bb-hero-actions">
          <a
            :href="requestDemo.url"
            target="_blank"
            rel="noopener noreferrer"
            class="bb-cta-btn"
            @click="sendInformationToGoogleAnalitics('request_demo')">
            Request Demo →
          </a>
          <a href="mailto:info@bandhoo.com" class="bb-ghost-btn">info@bandhoo.com</a>
        </div>
        <div class="bb-footer-cta-byline">Helpline: 6364-432-123 · bandhoo.com</div>
      </div>
    </section>

  </main>
</template>

<script>
import InfiniteScroll from "@/components/InfiniteScroll.vue"
import { homeClients } from '@/assets/db/home-data'
import { blogPosts } from '@/assets/db/blog-data'
import { appUtils, requestDemo } from '@/assets/db/static-data'
import { GoogleAdsConversions } from '@/assets/db/config'

const TOPIC_COLORS = {
  Company: '#0a66c2',
  CONSTRA: '#1e6f5c',
  PLM: '#5a4fcf',
  Manpower: '#b3631c',
  Industry: '#475569',
  Careers: '#c2255c'
}

export default {
  name: 'BandhooHome',
  components: { InfiniteScroll },
  data() {
    return {
      homeClients,
      blogPosts,
      appUtils,
      requestDemo,
      heroStats: [
        { val: '40+', txt: 'Enterprise Clients' },
        { val: '4', txt: 'Solution Suites' },
        { val: '100%', txt: 'Digital Workflows' },
        { val: 'PAN-India', txt: 'Coverage' }
      ],
      pillars: [
        {
          name: 'CONSTRA',
          tag: 'Build & Survey',
          desc: 'AI-based construction project monitoring with drones & 360° cameras.',
          url: '/solutions/ai_project_monitoring',
          gtag: 'ai_project_monitoring',
          icon: '<g stroke="currentColor" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M-15 16 H15"/><path d="M-6 16 V-13 H11"/><path d="M-6 -13 L-14 -5"/><path d="M9 -13 V-5"/><rect x="5" y="-5" width="8" height="6" fill="currentColor" stroke="none"/></g>'
        },
        {
          name: 'PLM',
          tag: 'Cost Manager',
          desc: 'Project Lifecycle Manager — the only cloud SaaS to manage costs end-to-end.',
          url: '/solutions/developer',
          gtag: 'website_developer',
          icon: '<g stroke="currentColor" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="-15" y="-15" width="30" height="30" rx="4"/><path d="M-9 6 L-3 -2 L3 3 L10 -8"/><circle cx="-9" cy="6" r="1.6" fill="currentColor"/><circle cx="-3" cy="-2" r="1.6" fill="currentColor"/><circle cx="3" cy="3" r="1.6" fill="currentColor"/><circle cx="10" cy="-8" r="1.6" fill="currentColor"/></g>'
        },
        {
          name: 'Manpower Mobilization',
          tag: 'Workers & Contractors',
          desc: 'Access to around 5L workers and contractors to solve site manpower shortages.',
          url: '/solutions/contractor',
          gtag: 'website_contractor',
          icon: '<g fill="currentColor"><circle cx="-11" cy="-5" r="4.6"/><path d="M-19 10 a8 8 0 0 1 16 0 z"/><circle cx="11" cy="-5" r="4.6"/><path d="M3 10 a8 8 0 0 1 16 0 z"/><circle cx="0" cy="-9" r="6"/><path d="M-11 13 a11 10 0 0 1 22 0 z"/></g>'
        },
        {
          name: 'Digital PMC',
          tag: 'Managed Service',
          desc: 'Tech-enabled project management consultancy — we run your project on the Bandhoo stack.',
          url: requestDemo.url,
          external: true,
          gtag: 'request_demo',
          icon: '<g stroke="currentColor" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="-12" y="-15" width="24" height="30" rx="3"/><rect x="-6" y="-18" width="12" height="6" rx="2" fill="currentColor" stroke="none"/><path d="M-6 -4 L-2 0 L7 -9"/><path d="M-6 7 H6"/></g>'
        }
      ],
      constraPoints: [
        'AI-driven progress reports against WBS, automatically',
        'Drone & 360° camera captures across every construction stage',
        'AI assistants for defects, safety and housekeeping',
        'CAD overlays, RA-bill certification & CRM video tours'
      ],
      plmModules: [
        { code: 'CPVM', name: 'Contracting, Procurement & Vendor Management' },
        { code: 'BESM', name: 'Budgeting, Estimate & Masters Management' },
        { code: 'BICM', name: 'Billings, Invoices & Contract Management' }
      ],
      plmComparison: [
        { task: 'BOQ preparation & management', without: 'Error-prone, time-consuming', with: '100% accurate, very easy' },
        { task: 'Bid comparison report', without: '1–2 days', with: 'Click of a button' },
        { task: 'RA-bill certification & payments', without: '30–60 days', with: 'Within 7 days' },
        { task: 'Near-term cash-flow provisioning', without: '2–3 days, inaccurate', with: '15 minutes, accurate' },
        { task: 'Reverse-auction savings', without: 'Missing', with: '2–10% savings' }
      ],
      manpowerPoints: [
        'Full labour compliance — WCI, PF, BOCW cess & Labour License',
        'Structured batch deployment — first batch on site in ~2 weeks',
        'PPE & accommodation arranged where not available on-site',
        'Weekly worker advances, salaries processed by the 10th'
      ],
      workforceTiers: [
        { val: '10,000+', label: 'Mid-sized contractors', sub: '₹10cr–₹100cr turnover', width: 62 },
        { val: '1.5 Lakh+', label: 'Labour contractors', sub: 'All trades · major cities', width: 82 },
        { val: '3.3 Lakh+', label: 'Skilled & unskilled workers', sub: 'On site in 2–3 weeks', width: 100 }
      ],
      trades: ['Civil & Structure', 'MEP', 'Finishing', 'Support & Operations'],
      pmcPoints: [
        'Dedicated project management team on the ground',
        'AI progress monitoring, defect & safety tracking',
        'Digitised budgeting, procurement, billing & cash-flow',
        'Contractor & manpower sourcing, anywhere in India'
      ],
      pmcStack: [
        { code: '01', name: 'CONSTRA', desc: 'AI progress monitoring via drones & 360° cameras' },
        { code: '02', name: 'PLM', desc: 'End-to-end cost, procurement & billing control' },
        { code: '03', name: 'Manpower', desc: 'Contractors & workers sourced from our network' }
      ],
      founders: [
        {
          name: 'Prashant Gupta',
          role: 'Founder & CEO',
          img: '/img/team/Prashant.jpg',
          li: 'https://www.linkedin.com/in/prashant-gupta-bandhoo/',
          bio: '25+ years across technology and construction.',
          points: [
            'Ex-Partner at McKinsey — leader of Infra & Construction',
            'Ex-CEO of Emaar India',
            "Ex-CEO of Aditya Birla Group's ecommerce foray",
            'IIT Delhi (Computer Science) & IIM Ahmedabad'
          ]
        },
        {
          name: 'Sridhar Sundaram',
          role: 'Founder & CTO',
          img: '/img/team/Sridhar.webp',
          imgClass: 'bb-founder-img-top',
          li: 'https://www.linkedin.com/in/sridharsundaram/',
          bio: '30+ years of experience in technology.',
          points: [
            'Ex-Google (2004–2012, California & Bangalore)',
            'Serial entrepreneur — Edtech, Healthtech, Analytics',
            'IIT Delhi (Computer Science)',
            'Cornell University, USA (PhD)'
          ]
        }
      ],
      leadership: [
        { name: 'Gaurav Jajodia', role: 'Leadership Team', li: 'https://www.linkedin.com/in/gauravjajodia/', image: 'Gaurav.jpg' },
        { name: 'Nikhilesh Dangayach', role: 'Leadership Team', li: 'https://www.linkedin.com/in/nikhilesh-dangayach-404979a2/', image: 'Nikhilesh.webp' },
        { name: 'Noopur Arora', role: 'Leadership Team', li: 'https://www.linkedin.com/in/noopur-arora-60409486/', image: 'Noopur.jpg' },
        { name: 'Revanna S', role: 'Leadership Team', li: 'https://www.linkedin.com/in/revannas/', image: 'Revanna.jpg' },
        { name: 'Sumanth J', role: 'Leadership Team', li: 'https://www.linkedin.com/in/sumanth-jay/', image: 'Sumanth.jpg' },
        { name: 'Vikas PN', role: 'Leadership Team', li: 'https://www.linkedin.com/in/vikas-pn/', image: 'Vikas.webp' }
      ]
    }
  },
  computed: {
    latestPosts() {
      return this.blogPosts.filter(post => post.topic !== 'Careers').slice(0, 6)
    }
  },
  methods: {
    topicColor(topic) {
      return TOPIC_COLORS[topic] || '#003366'
    },
    lead(text) {
      const match = (text || '').match(/^(.{12,150}?[.!?])(\s|$)/)
      return match ? match[1] : (text || '').split(/\s+/).slice(0, 22).join(' ') + '…'
    },
    sendInformationToGoogleAnalitics(information) {
      if (information === 'request_demo') {
        this.$gtag.event('conversion', GoogleAdsConversions.REQUEST_DEMO_CLICK);
      } else if (information === 'website_playstore') {
        this.$gtag.event('conversion', GoogleAdsConversions.WEBSITE_PLAYSTORE);
      } else if (information === 'website_appstore') {
        this.$gtag.event('conversion', GoogleAdsConversions.WEBSITE_APPSTORE);
      }
      this.$gtag.event(information);
    }
  }
}
</script>

<style scoped>
.bb-home {
  background: #fff;
  color: var(--bb-body);
  line-height: 1.6;
}

.bb-container {
  max-width: 1200px;
  margin: 0 auto;
}

.bb-container-narrow {
  max-width: 1080px;
}

.bb-section {
  padding: clamp(56px, 7vw, 88px) 24px;
}

.bb-bg-white {
  background: #fff;
}

.bb-bg-alt {
  background: var(--bb-bg-alt);
}

.bb-accent {
  color: var(--bb-lime);
}

.bb-arrow {
  display: inline-block;
  transition: transform 0.25s;
}

/* ── hero ───────────────────────────────────────────────── */
.bb-hero {
  background: linear-gradient(155deg, var(--bb-navy-light) 0%, var(--bb-navy) 50%, var(--bb-navy-deep) 100%);
  padding: clamp(72px, 9vw, 116px) 24px clamp(56px, 7vw, 88px);
  text-align: center;
  position: relative;
  overflow: hidden;
}

.bb-hero-dots {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px);
  background-size: 26px 26px;
}

.bb-hero-glow {
  position: absolute;
  bottom: -90px;
  left: 50%;
  transform: translateX(-50%);
  width: 760px;
  height: 260px;
  background: radial-gradient(ellipse, rgba(179, 211, 60, 0.11) 0%, transparent 70%);
  pointer-events: none;
}

.bb-hero-inner {
  position: relative;
}

.bb-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 30px;
  padding: 7px 16px 7px 8px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.06);
}

.bb-hero-badge-tag {
  background: var(--bb-lime);
  color: var(--bb-navy);
  font-weight: 900;
  font-size: 10px;
  letter-spacing: 1px;
  padding: 3px 9px;
  border-radius: 20px;
}

.bb-hero-badge-txt {
  color: rgba(255, 255, 255, 0.78);
  font-size: 12.5px;
  letter-spacing: 0.3px;
}

.bb-hero-headline {
  color: #fff;
  font-size: clamp(32px, 5.4vw, 62px);
  font-weight: 900;
  line-height: 1.08;
  margin: 0 auto 22px;
  max-width: 960px;
  letter-spacing: -1.4px;
  text-wrap: balance;
}

.bb-hero-sub {
  color: rgba(255, 255, 255, 0.62);
  font-size: clamp(16px, 2vw, 21px);
  margin: 0 auto 42px;
  letter-spacing: 0.2px;
  max-width: 680px;
  line-height: 1.55;
}

.bb-hero-actions {
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
}

.bb-cta-btn {
  background: var(--bb-lime);
  color: var(--bb-navy);
  border-radius: 12px;
  padding: 17px 46px;
  font-weight: 900;
  font-size: 16.5px;
  letter-spacing: 0.3px;
  box-shadow: 0 6px 22px rgba(179, 211, 60, 0.22);
  transition: all 0.22s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.bb-cta-btn:hover {
  background: var(--bb-lime-hover);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 16px 40px rgba(179, 211, 60, 0.33);
}

.bb-ghost-btn {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 12px;
  padding: 17px 36px;
  font-weight: 700;
  font-size: 16.5px;
  letter-spacing: 0.3px;
}

.bb-hero-stats {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(24px, 5vw, 84px);
  margin-top: 72px;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.bb-hero-stat {
  text-align: center;
}

.bb-hero-stat-val {
  color: var(--bb-lime);
  font-size: clamp(26px, 3vw, 36px);
  font-weight: 900;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.bb-hero-stat-txt {
  color: rgba(255, 255, 255, 0.45);
  font-size: 11px;
  letter-spacing: 1.4px;
  margin-top: 8px;
  text-transform: uppercase;
}

/* ── client marquee ─────────────────────────────────────── */
.bb-marquee {
  background: #fff;
  padding: 34px 0 38px;
  border-bottom: 1px solid var(--bb-border);
  overflow: hidden;
}

.bb-marquee-title {
  text-align: center;
  color: var(--bb-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 0 0 28px;
}

.bb-marquee :deep(.clientLogo) {
  height: 46px;
  max-width: 130px;
  opacity: 0.92;
  transition: transform 0.25s, opacity 0.25s;
}

.bb-marquee :deep(.clientLogo:hover) {
  opacity: 1;
  transform: scale(1.06);
}

/* ── section header ─────────────────────────────────────── */
.bb-section-header {
  text-align: center;
  margin-bottom: 52px;
}

.bb-section-pill {
  display: inline-block;
  background: var(--bb-lime-bg);
  color: var(--bb-lime-text);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 4px 13px;
  border-radius: 20px;
  margin-bottom: 14px;
}

.bb-section-title {
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 900;
  color: var(--bb-navy);
  margin: 0 0 12px;
  line-height: 1.18;
  letter-spacing: -0.5px;
  text-wrap: balance;
}

.bb-section-sub {
  color: var(--bb-body);
  font-size: 16px;
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.65;
}

.bb-section-rule {
  width: 36px;
  height: 3px;
  background: var(--bb-lime);
  border-radius: 2px;
  margin: 16px auto 0;
}

/* ── pillars overview ───────────────────────────────────── */
.bb-overview {
  padding-bottom: 30px;
}

.bb-pillars-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
}

.bb-pillar-card {
  display: block;
  background: #fff;
  border-radius: 18px;
  padding: 34px 30px 30px;
  border: 1px solid var(--bb-border-light);
  border-top: 4px solid var(--bb-navy);
  box-shadow: var(--bb-shadow);
  transition: transform 0.28s, box-shadow 0.28s;
}

.bb-pillar-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--bb-shadow-hover);
}

.bb-pillar-card:hover .bb-arrow {
  transform: translateX(4px);
}

.bb-pillar-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
}

.bb-pillar-icon {
  width: 60px;
  height: 60px;
  border-radius: 15px;
  background: var(--bb-lime-bg);
  color: var(--bb-navy);
  display: flex;
  align-items: center;
  justify-content: center;
}

.bb-pillar-tag {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--bb-navy);
  background: var(--bb-lime-bg);
  padding: 5px 11px;
  border-radius: 20px;
  text-align: center;
}

.bb-pillar-name {
  font-size: 20px;
  font-weight: 900;
  color: var(--bb-navy);
  margin: 0 0 9px;
  letter-spacing: -0.3px;
}

.bb-pillar-desc {
  font-size: 14px;
  color: var(--bb-body);
  margin: 0 0 18px;
  line-height: 1.6;
}

.bb-learn-more {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--bb-navy);
  font-weight: 800;
  font-size: 13.5px;
}

/* ── pillar deep-dives ──────────────────────────────────── */
.bb-pillar {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
}

.bb-pillar-eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.bb-pillar-num {
  font-size: 13px;
  font-weight: 900;
  color: var(--bb-navy);
  font-variant-numeric: tabular-nums;
  border: 2px solid var(--bb-navy);
  border-radius: 9px;
  padding: 4px 9px;
  line-height: 1;
}

.bb-pillar-tagline {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--bb-navy);
}

.bb-pillar-heading {
  font-size: clamp(26px, 3.2vw, 36px);
  font-weight: 900;
  color: var(--bb-navy);
  margin: 0 0 12px;
  letter-spacing: -0.6px;
  line-height: 1.12;
}

.bb-sub-heading {
  font-size: clamp(20px, 2.4vw, 26px);
  font-weight: 900;
  color: var(--bb-navy);
  margin: 0 0 14px;
  letter-spacing: -0.4px;
  line-height: 1.2;
}

.bb-pillar-lede {
  font-size: 17px;
  color: var(--bb-heading);
  font-weight: 600;
  margin: 0 0 20px;
  line-height: 1.5;
}

.bb-pillar-body {
  font-size: 14.5px;
  color: var(--bb-body);
  line-height: 1.65;
  margin: 0 0 22px;
}

.bb-pillar-body strong {
  color: var(--bb-heading);
}

.bb-checklist {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bb-check-row {
  display: flex;
  gap: 11px;
  align-items: flex-start;
}

.bb-check {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  margin-top: 1px;
  background: var(--bb-lime-bg);
  color: var(--bb-lime-text);
  font-weight: 900;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bb-check-txt {
  font-size: 14.5px;
  color: var(--bb-body);
  line-height: 1.5;
}

.bb-pillar-cta {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-top: 26px;
  background: var(--bb-lime);
  color: var(--bb-navy);
  border-radius: 11px;
  padding: 13px 26px;
  font-weight: 800;
  font-size: 14.5px;
  box-shadow: 0 4px 14px rgba(179, 211, 60, 0.25);
  transition: all 0.22s;
}

.bb-pillar-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(179, 211, 60, 0.4);
}

.bb-pillar-cta:hover .bb-arrow {
  transform: translateX(4px);
}

/* ── constra visual ─────────────────────────────────────── */
.bb-constra-visual {
  position: relative;
}

.bb-constra-frame {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  border: 6px solid #fff;
  position: relative;
}

.bb-constra-img {
  width: 100%;
  display: block;
  aspect-ratio: 16 / 11;
  object-fit: cover;
}

.bb-constra-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, transparent 55%, rgba(0, 0, 0, 0.45));
}

.bb-chip {
  position: absolute;
  background: #fff;
  border-radius: 12px;
  padding: 10px 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.16);
}

.bb-chip-tl {
  top: 18px;
  left: -16px;
}

.bb-chip-br {
  bottom: 22px;
  right: -14px;
}

.bb-chip-title {
  font-size: 14px;
  font-weight: 900;
  color: var(--bb-navy);
}

.bb-chip-sub {
  font-size: 11px;
  color: var(--bb-muted);
  font-weight: 600;
}

/* ── plm ────────────────────────────────────────────────── */
.bb-mods {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.bb-mod {
  background: #fff;
  border: 1px solid var(--bb-border-light);
  border-top: 3px solid var(--bb-navy);
  border-radius: 12px;
  padding: 14px;
}

.bb-mod-code {
  font-size: 14px;
  font-weight: 900;
  color: var(--bb-navy);
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}

.bb-mod-txt {
  font-size: 11.5px;
  color: var(--bb-body);
  line-height: 1.45;
}

.bb-cmp {
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--bb-shadow);
  border: 1px solid var(--bb-border-light);
}

.bb-cmp-row {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr;
}

.bb-cmp-head {
  background: var(--bb-navy);
}

.bb-cmp-activity,
.bb-cmp-without,
.bb-cmp-with {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}

.bb-cmp-activity {
  padding: 14px 18px;
  letter-spacing: 1px;
}

.bb-cmp-without {
  padding: 14px 12px;
  letter-spacing: 0.5px;
  text-align: center;
}

.bb-cmp-with {
  padding: 14px 12px;
  letter-spacing: 0.5px;
  text-align: center;
  color: var(--bb-lime);
}

.bb-cmp-body {
  border-top: 1px solid var(--bb-border);
  background: #fff;
}

.bb-cmp-body.odd {
  background: var(--bb-bg-alt);
}

.bb-cmp-task {
  padding: 13px 18px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--bb-heading);
  line-height: 1.35;
}

.bb-cmp-bad {
  padding: 13px 12px;
  font-size: 12px;
  color: #b91c1c;
  text-align: center;
  line-height: 1.4;
}

.bb-cmp-good {
  padding: 13px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #15803d;
  text-align: center;
  line-height: 1.4;
}

.bb-cmp-foot {
  background: var(--bb-navy);
  color: #fff;
  text-align: center;
  padding: 12px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

/* ── manpower ───────────────────────────────────────────── */
.bb-edge {
  margin-top: 24px;
  display: flex;
  gap: 13px;
  align-items: center;
  background: var(--bb-navy);
  border-radius: 14px;
  padding: 15px 18px;
}

.bb-edge-tag {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: var(--bb-lime);
  color: var(--bb-navy);
  border-radius: 7px;
  padding: 6px 9px;
  line-height: 1;
}

.bb-edge-title {
  font-size: 14px;
  font-weight: 800;
  color: #fff;
  line-height: 1.3;
}

.bb-edge-txt {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.5;
  margin-top: 2px;
}

.bb-edge-txt strong {
  color: #fff;
}

.bb-pyramid {
  background: var(--bb-bg-alt);
  border-radius: 18px;
  padding: clamp(24px, 3.2vw, 34px);
  border: 1px solid var(--bb-border-light);
}

.bb-pyramid-title {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--bb-muted);
  text-align: center;
  margin: 0 0 5px;
}

.bb-pyramid-sub {
  font-size: 12.5px;
  color: var(--bb-body);
  text-align: center;
  margin: 0 0 24px;
  line-height: 1.45;
}

.bb-pyramid-tiers {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.bb-tier {
  min-width: 220px;
  background: linear-gradient(135deg, var(--bb-navy-light), var(--bb-navy-deep));
  border-radius: 12px;
  padding: 15px 20px;
  text-align: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.13);
}

.bb-tier-val {
  font-size: clamp(20px, 2.6vw, 26px);
  font-weight: 900;
  color: var(--bb-lime);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.bb-tier-label {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  margin-top: 6px;
}

.bb-tier-sub {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.bb-trades {
  margin-top: 40px;
  text-align: center;
}

.bb-trades-title {
  color: var(--bb-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 0 0 16px;
}

.bb-trades-chips {
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 760px;
  margin: 0 auto;
}

.bb-trade-chip {
  background: var(--bb-bg-alt);
  border: 1px solid var(--bb-border-light);
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  color: var(--bb-heading);
}

.bb-offers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 48px;
}

.bb-offer {
  border-radius: 16px;
  padding: clamp(24px, 3vw, 32px);
  display: flex;
  flex-direction: column;
}

.bb-offer-dark {
  background: var(--bb-navy);
}

.bb-offer-light {
  background: #fff;
  border: 1px solid var(--bb-border-light);
  box-shadow: var(--bb-shadow);
}

.bb-offer-title {
  font-size: clamp(19px, 2.2vw, 23px);
  font-weight: 900;
  color: var(--bb-navy);
  margin: 0 0 10px;
  line-height: 1.25;
}

.bb-offer-title-light {
  color: #fff;
}

.bb-offer-txt {
  font-size: 14px;
  color: var(--bb-body);
  margin: 0 0 22px;
  line-height: 1.6;
  flex: 1;
}

.bb-offer-txt-light {
  color: rgba(255, 255, 255, 0.72);
}

.bb-offer-cta-lime,
.bb-offer-cta-navy {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  align-self: flex-start;
  border-radius: 11px;
  padding: 13px 26px;
  font-weight: 800;
  font-size: 14px;
}

.bb-offer-cta-lime {
  background: var(--bb-lime);
  color: var(--bb-navy);
}

.bb-offer-cta-navy {
  background: var(--bb-navy);
  color: #fff;
}

/* ── digital pmc ────────────────────────────────────────── */
.bb-stack {
  background: #fff;
  border-radius: 18px;
  padding: 26px;
  border: 1px solid var(--bb-border-light);
  box-shadow: var(--bb-shadow);
}

.bb-stack-title {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--bb-muted);
  margin: 0 0 18px;
}

.bb-stack-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bb-stack-row {
  display: flex;
  gap: 14px;
  align-items: center;
  background: var(--bb-bg-alt);
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid var(--bb-border-light);
}

.bb-stack-num {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--bb-navy);
  color: #fff;
  font-weight: 900;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bb-stack-name {
  font-size: 15px;
  font-weight: 900;
  color: var(--bb-navy);
  line-height: 1.2;
}

.bb-stack-desc {
  font-size: 12.5px;
  color: var(--bb-body);
  line-height: 1.4;
}

.bb-stack-note {
  margin-top: 16px;
  padding: 15px 18px;
  border-radius: 12px;
  background: var(--bb-lime-bg);
  border: 1px solid rgba(179, 211, 60, 0.4);
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--bb-lime-text);
  line-height: 1.4;
}

.bb-stack-note-arrow {
  font-size: 22px;
  font-weight: 900;
}

/* ── team ───────────────────────────────────────────────── */
.bb-founders-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
}

.bb-founder {
  background: #fff;
  border-radius: 18px;
  padding: 30px;
  border: 1px solid var(--bb-border-light);
  box-shadow: var(--bb-shadow);
  display: flex;
  flex-direction: column;
}

.bb-founder-head {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 22px;
}

.bb-founder-img {
  width: 78px;
  height: 78px;
  border-radius: 14px;
  object-fit: cover;
  object-position: center;
  border: 3px solid #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.bb-founder-img-top {
  object-position: 50% 12%;
}

.bb-founder-meta {
  flex: 1;
}

.bb-founder-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bb-founder-name {
  font-size: 19px;
  font-weight: 900;
  color: var(--bb-navy);
  line-height: 1.2;
}

.bb-li {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--bb-lime-bg);
  color: #0a66c2;
  flex-shrink: 0;
  transition: background 0.18s, color 0.18s;
}

.bb-li-lg {
  width: 30px;
  height: 30px;
  border-radius: 8px;
}

.bb-li:hover {
  background: #0a66c2;
  color: #fff;
}

.bb-founder-role {
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--bb-lime-text);
  margin-top: 5px;
}

.bb-founder-bio {
  font-size: 13px;
  color: var(--bb-body);
  margin-top: 6px;
}

.bb-founder-points {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid var(--bb-border);
  padding-top: 20px;
}

.bb-founder-point {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13.5px;
  color: var(--bb-body);
  line-height: 1.5;
}

.bb-founder-tick {
  color: var(--bb-lime-text);
  font-weight: 900;
  font-size: 14px;
  line-height: 1.4;
  flex-shrink: 0;
}

.bb-team {
  margin-top: 30px;
}

.bb-team-title {
  text-align: center;
  color: var(--bb-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 0 0 22px;
}

.bb-team-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.bb-team-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  border: 1px solid var(--bb-border-light);
  box-shadow: var(--bb-shadow);
  display: flex;
  align-items: center;
  gap: 14px;
}

.bb-team-img {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}

.bb-team-meta {
  flex: 1;
  min-width: 0;
}

.bb-team-name {
  font-size: 15px;
  font-weight: 800;
  color: var(--bb-navy);
  line-height: 1.2;
}

.bb-team-role {
  font-size: 11.5px;
  color: var(--bb-muted);
  margin-top: 2px;
}

/* ── blog ───────────────────────────────────────────────── */
.bb-blog-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.bb-blog-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--bb-border-light);
  box-shadow: var(--bb-shadow);
  display: flex;
  flex-direction: column;
  transition: transform 0.25s, box-shadow 0.25s;
}

.bb-blog-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.13);
}

.bb-blog-stripe {
  height: 8px;
}

.bb-blog-body {
  padding: 22px 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.bb-blog-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bb-blog-topic {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  border-radius: 20px;
  padding: 3px 10px;
}

.bb-blog-date {
  margin-left: auto;
  font-size: 11.5px;
  color: var(--bb-muted);
}

.bb-blog-lead {
  font-size: 14.5px;
  color: var(--bb-heading);
  margin: 0;
  line-height: 1.45;
  font-weight: 700;
}

.bb-blog-excerpt {
  font-size: 13.5px;
  color: var(--bb-body);
  margin: 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bb-blog-read {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 4px;
  font-size: 13px;
  font-weight: 800;
  color: var(--bb-lime-text);
}

.bb-blog-more {
  text-align: center;
  margin-top: 34px;
}

.bb-blog-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  background: var(--bb-navy);
  color: #fff;
  border-radius: 11px;
  padding: 13px 28px;
  font-size: 14px;
}

/* ── trust & download ───────────────────────────────────── */
.bb-trust {
  background: var(--bb-bg-alt);
  padding: clamp(48px, 6vw, 72px) 24px;
  border-top: 1px solid var(--bb-border-light);
}

.bb-trust-inner {
  max-width: 860px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.bb-quote {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.bb-quote-bar {
  width: 4px;
  flex-shrink: 0;
  align-self: stretch;
  background: var(--bb-lime);
  border-radius: 4px;
}

.bb-quote-txt {
  font-size: clamp(16px, 2vw, 20px);
  font-style: italic;
  font-weight: 600;
  color: var(--bb-heading);
  line-height: 1.55;
  margin: 0;
}

.bb-quote-txt-sm {
  font-size: clamp(15px, 1.8vw, 18px);
}

.bb-stamps {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
}

.bb-stamp {
  border: 1px solid var(--bb-border-light);
  border-radius: 12px;
  padding: 16px;
  background: #fff;
  box-shadow: var(--bb-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 132px;
  height: 132px;
}

.bb-stamp img {
  width: 100%;
  height: auto;
}

.bb-download {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.bb-store-badges {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: center;
}

.bb-store-badge {
  display: inline-flex;
}

.bb-store-badge img {
  height: 54px;
  width: auto;
}

/* ── footer cta ─────────────────────────────────────────── */
.bb-footer-cta {
  background: linear-gradient(155deg, var(--bb-navy-light) 0%, var(--bb-navy) 50%, var(--bb-navy-deep) 100%);
  padding: clamp(60px, 8vw, 92px) 24px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.bb-footer-cta-glow {
  position: absolute;
  top: -90px;
  left: 50%;
  transform: translateX(-50%);
  width: 760px;
  height: 260px;
  background: radial-gradient(ellipse, rgba(179, 211, 60, 0.09) 0%, transparent 70%);
  pointer-events: none;
}

.bb-footer-cta-inner {
  max-width: 720px;
  margin: 0 auto;
  position: relative;
}

.bb-footer-cta-eyebrow {
  color: var(--bb-lime);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 18px;
}

.bb-footer-cta-title {
  color: #fff;
  font-size: clamp(27px, 4vw, 46px);
  font-weight: 900;
  margin: 0 0 16px;
  line-height: 1.18;
  letter-spacing: -0.6px;
  text-wrap: balance;
}

.bb-footer-cta-sub {
  color: rgba(255, 255, 255, 0.6);
  font-size: 17px;
  margin: 0 0 40px;
  line-height: 1.6;
}

.bb-footer-cta-byline {
  margin-top: 30px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
}

/* ── responsive ─────────────────────────────────────────── */
@media (max-width: 980px) {
  .bb-pillars-grid {
    grid-template-columns: 1fr 1fr;
    max-width: 620px;
    margin: 0 auto;
  }
}

@media (max-width: 880px) {
  .bb-pillar {
    grid-template-columns: 1fr;
    gap: 36px;
  }

  .bb-blog-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 820px) {
  .bb-founders-grid {
    grid-template-columns: 1fr;
    max-width: 520px;
    margin: 0 auto;
  }
}

@media (max-width: 760px) {
  .bb-team-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 720px) {
  .bb-offers {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .bb-chip-tl {
    left: 6px;
  }

  .bb-chip-br {
    right: 6px;
  }

  .bb-cmp-row {
    grid-template-columns: 1.2fr 1fr 1fr;
  }

  .bb-cmp-task,
  .bb-cmp-activity {
    padding-left: 12px;
    padding-right: 8px;
  }
}

@media (max-width: 560px) {
  .bb-blog-grid {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
}

@media (max-width: 540px) {
  .bb-mods {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .bb-pillars-grid {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin: 0 auto;
  }
}

@media (max-width: 460px) {
  .bb-team-grid {
    grid-template-columns: 1fr;
    max-width: 340px;
    margin: 0 auto;
  }
}
</style>
